import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { FilterDef } from '@nucleus-commerce/admin-kit/index-table';

import { attributesQueryOptions } from '@/features/dictionary';
import { session } from '@/shared/session/session';

import { productFilterDefs } from '../components/product-filter-defs';
import type { ProductPickerScope } from '../lib/product-filters';

export interface UseProductFilterDefsOptions {
  /** Filter keys to leave out (`status` when statuses are pinned). */
  omit?: readonly string[];
}

/**
 * `productFilterDefs` with what it needs loaded: the session's stores and the attribute
 * dictionary (attribute chips appear once it has loaded).
 */
export function useProductFilterDefs<TData = never>(
  scope: ProductPickerScope,
  { omit = [] }: UseProductFilterDefsOptions = {},
): Array<FilterDef<TData>> {
  const { t } = useTranslation(['resourcePickers', 'common']);
  const queryClient = useQueryClient();
  const { me } = session.useSession();
  const stores = me?.stores;
  const attributes = useQuery(attributesQueryOptions()).data;
  const storeId = scope.kind === 'store' ? scope.storeId : null;
  const omitted = omit.join(',');
  return useMemo(
    () =>
      productFilterDefs<TData>(storeId ? { kind: 'store', storeId } : { kind: 'org' }, {
        t,
        queryClient,
        stores: stores ?? [],
        attributes: attributes ?? [],
        omit: omitted ? omitted.split(',') : [],
      }),
    [attributes, omitted, queryClient, storeId, stores, t],
  );
}
